'use client';

import { useIsLoggedIn } from '@/entities/auth/hooks/useIsLoggedIn';
import { useVisitShortUrlMutation } from '@/entities/salePage';
import { useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useRef } from 'react';

const VLD_STORAGE_KEY = 'vld_pending';

function parseSafeMap(raw: string | null): Record<string, string> {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (
      Array.isArray(parsed) ||
      typeof parsed !== 'object' ||
      parsed === null
    ) {
      return {};
    }
    return parsed;
  } catch {
    return {};
  }
}

function getStoredVld(salePageId: number): string | null {
  return (
    parseSafeMap(localStorage.getItem(VLD_STORAGE_KEY))[String(salePageId)] ??
    null
  );
}

function saveVldToStorage(salePageId: number, vld: string) {
  try {
    const map = parseSafeMap(localStorage.getItem(VLD_STORAGE_KEY));
    map[String(salePageId)] = vld;
    localStorage.setItem(VLD_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore storage errors
  }
}

function removeVldFromStorage(salePageId: number) {
  try {
    const map = parseSafeMap(localStorage.getItem(VLD_STORAGE_KEY));
    delete map[String(salePageId)];
    if (Object.keys(map).length > 0) {
      localStorage.setItem(VLD_STORAGE_KEY, JSON.stringify(map));
    } else {
      localStorage.removeItem(VLD_STORAGE_KEY);
    }
  } catch {
    // ignore storage errors
  }
}

interface VldHandlerProps {
  salePageId: number;
}

const VldHandlerInner = ({ salePageId }: VldHandlerProps) => {
  const searchParams = useSearchParams();
  const { isLoggedIn, isLoading } = useIsLoggedIn();
  const { mutate: visitShortUrl } = useVisitShortUrlMutation();
  // history.replaceState는 useSearchParams를 업데이트하지 않아 searchParams가 stale 상태로 남으므로,
  // isLoggedIn 변경 등으로 effect가 재실행될 때 mutate 중복 호출을 방지
  const isSentRef = useRef(false);

  // _vld 처리 effect:
  // 1) URL에 _vld가 있으면 localStorage에 저장 후 URL에서 제거
  // 2) 로그인 상태면 저장된 vld를 API로 전송, 성공 시 localStorage 정리
  // 3) 비로그인이면 localStorage에 보관 → 로그인 후 재방문 시 전송
  useEffect(() => {
    if (isLoading) return;

    // URL에서 _vld 추출 → 스토리지 저장 및 URL 정리
    // history.replaceState를 사용하여 searchParams 변경 없이 URL만 정리
    const vldFromUrl = searchParams.get('_vld');
    if (vldFromUrl) {
      saveVldToStorage(salePageId, vldFromUrl);

      const url = new URL(window.location.href);
      url.searchParams.delete('_vld');
      window.history.replaceState(window.history.state, '', url.toString());
    }

    // 로그인 상태면 스토리지의 vld를 전송
    if (isLoggedIn && !isSentRef.current) {
      const vld = vldFromUrl ?? getStoredVld(salePageId);
      const linkManagementId = vld ? Number(vld) : NaN;

      if (!Number.isNaN(linkManagementId)) {
        isSentRef.current = true;
        removeVldFromStorage(salePageId);
        visitShortUrl(linkManagementId, {
          onError: () => {
            saveVldToStorage(salePageId, vld!);
            isSentRef.current = false;
          },
        });
      }
    }
  }, [isLoading, isLoggedIn, searchParams, salePageId, visitShortUrl]);

  return null;
};

export const VldHandler = ({ salePageId }: VldHandlerProps) => {
  return (
    <Suspense fallback={null}>
      <VldHandlerInner salePageId={salePageId} />
    </Suspense>
  );
};
